import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Ban,
  CheckCircle,
  ClipboardCheck,
  Eye,
  ExternalLink,
  FileText,
  Image as ImageIcon,
  Inbox,
  LogIn,
  Mail,
  MessageSquare,
  Shield,
  Star,
  User as UserIcon,
  type LucideIcon,
} from 'lucide-react';
import {
  CONTACT_INQUIRY_CATEGORY_LABELS,
  USER_STATUS_LABELS,
  VERIFICATION_LABELS,
  type ContactInquiryCategory,
  type UserStatus,
} from '@kulum/shared';
import {
  fetchUserOperationDetail,
  restoreSuspendedUser,
  suspendUser,
  type AdminUserOperationDetail,
} from '../../lib/userService';
import {
  buildImpersonationUrl,
  startUserImpersonation,
} from '../../lib/impersonationService';
import { useAuthStore } from '@/stores/authStore';
import { Loading, Badge, Button, Modal } from '../../components/ui';

const statusVariant: Record<UserStatus, 'default' | 'success' | 'warning' | 'danger' | 'info'> = {
  pending: 'default',
  email_verified: 'info',
  under_review: 'warning',
  approved: 'success',
  rejected: 'danger',
  suspended: 'danger',
  withdrawn: 'default',
};

const fieldLabels: Record<string, string> = {
  birthDate: '生年月日',
  nationality: '国籍',
  bloodType: '血液型',
  bloodTypeRh: 'Rh',
  birthplace: '出身地',
  residence: '居住地',
  education: '学歴',
  level: '区分',
  schoolName: '学校名',
  faculty: '学部・学科',
  status: '状態',
  annualIncome: '年収',
  maritalStatus: '婚姻歴',
  children: '子供',
  sons: '息子',
  daughters: '娘',
  ancestry: '家系',
  vaccinations: 'ワクチン',
  influenza: 'インフルエンザ',
  covid: 'コロナ',
  height: '身長',
  weight: '体重',
  bodyType: '体型',
  hairType: '髪質',
  hairColor: '髪色',
  eyelid: 'まぶた',
  methods: '提供方法',
  compensation: '謝礼金',
  travelExpense: '交通費等',
  identityDisclosure: '身元の開示',
  consanguinityMeasures: '近親婚への配慮',
  rightToKnowOrigin: '出自を知る権利',
  sameCityFamilyDonation: '同市内一家族提供',
  donationAreas: '提供地域',
  preferredLocation: '提供地域の希望',
};

function formatDateTime(date?: Date): string {
  if (!date) {
    return '-';
  }

  return new Intl.DateTimeFormat('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function formatValue(value: unknown): string {
  if (value === undefined || value === null || value === '') {
    return '-';
  }

  if (value instanceof Date) {
    return formatDateTime(value);
  }

  if (typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function') {
    return formatDateTime(value.toDate() as Date);
  }

  if (Array.isArray(value)) {
    return value.length > 0 ? value.map(formatValue).join(' / ') : '-';
  }

  if (typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>)
      .map(([key, nestedValue]) => `${fieldLabels[key] ?? key}: ${formatValue(nestedValue)}`)
      .join(' / ');
  }

  return String(value);
}

function DetailTable({ rows }: { rows: { label: string; value: unknown }[] }) {
  return (
    <div className="overflow-hidden rounded-xl border border-admin-border bg-white">
      <table className="w-full text-left text-sm">
        <tbody className="divide-y divide-admin-border">
          {rows.map((row) => (
            <tr key={row.label}>
              <th className="w-32 bg-admin-surface px-3 py-2 align-top text-xs font-semibold text-admin-muted">
                {row.label}
              </th>
              <td className="break-words px-3 py-2 text-admin-ink">
                {formatValue(row.value)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DetailCard({
  title,
  icon: Icon,
  children,
}: {
  title: string;
  icon: LucideIcon;
  children: ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-admin-border bg-white p-5 shadow-sm">
      <div className="mb-4 flex items-center gap-2">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-admin-accent/10 text-admin-accent">
          <Icon className="h-5 w-5" />
        </div>
        <h2 className="text-lg font-bold text-admin-ink">{title}</h2>
      </div>
      {children}
    </section>
  );
}

function RecordSection({
  title,
  record,
}: {
  title: string;
  record?: Record<string, unknown>;
}) {
  if (!record || Object.keys(record).length === 0) {
    return (
      <div>
        <h3 className="mb-2 text-sm font-semibold text-admin-ink">{title}</h3>
        <p className="rounded-xl border border-admin-border bg-admin-surface px-3 py-3 text-sm text-admin-muted">
          未登録です。
        </p>
      </div>
    );
  }

  return (
    <div>
      <h3 className="mb-2 text-sm font-semibold text-admin-ink">{title}</h3>
      <DetailTable
        rows={Object.entries(record).map(([key, value]) => ({
          label: fieldLabels[key] ?? key,
          value,
        }))}
      />
    </div>
  );
}

function EmptyImageState({ label }: { label: string }) {
  return (
    <div className="flex h-32 items-center justify-center rounded-xl border border-dashed border-admin-border bg-admin-surface px-4 text-sm text-admin-muted">
      {label}は未設定です。
    </div>
  );
}

function ImagePreviewCard({
  imageUrl,
  label,
  variant = 'gallery',
}: {
  imageUrl: string;
  label: string;
  variant?: 'avatar' | 'gallery';
}) {
  const [hasLoadError, setHasLoadError] = useState(false);
  const imageClassName =
    variant === 'avatar'
      ? 'h-40 w-40 rounded-2xl object-cover'
      : 'h-44 w-full object-cover';
  const frameClassName =
    variant === 'avatar'
      ? 'flex h-44 items-center justify-center bg-admin-surface p-2'
      : 'bg-admin-surface';

  return (
    <a
      href={imageUrl}
      target="_blank"
      rel="noreferrer"
      className="group block overflow-hidden rounded-xl border border-admin-border bg-white transition hover:border-admin-accent"
    >
      <div className={frameClassName}>
        {hasLoadError ? (
          <div className="flex h-32 w-full items-center justify-center px-4 text-center text-sm text-admin-muted">
            画像を読み込めません。新しいタブで確認してください。
          </div>
        ) : (
          <img
            src={imageUrl}
            alt={label}
            className={imageClassName}
            loading="lazy"
            onError={() => setHasLoadError(true)}
          />
        )}
      </div>
      <div className="flex items-center justify-between gap-2 border-t border-admin-border px-3 py-2 text-xs font-medium text-admin-ink">
        <span className="truncate">{label}</span>
        <ExternalLink className="h-3.5 w-3.5 flex-shrink-0 text-admin-muted transition group-hover:text-admin-accent" />
      </div>
    </a>
  );
}

function UserImagesSection({
  profileImageUrl,
  additionalImages,
}: {
  profileImageUrl?: string;
  additionalImages: string[];
}) {
  return (
    <div className="space-y-5">
      <div>
        <h3 className="mb-2 text-sm font-semibold text-admin-ink">プロフィール画像</h3>
        {profileImageUrl ? (
          <ImagePreviewCard imageUrl={profileImageUrl} label="プロフィール画像" variant="avatar" />
        ) : (
          <EmptyImageState label="プロフィール画像" />
        )}
      </div>

      <div>
        <div className="mb-2 flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-admin-ink">追加画像</h3>
          <span className="text-xs text-admin-muted">{additionalImages.length}件</span>
        </div>
        {additionalImages.length > 0 ? (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {additionalImages.map((imageUrl, index) => (
              <ImagePreviewCard
                key={`${imageUrl}-${index}`}
                imageUrl={imageUrl}
                label={`追加画像 ${index + 1}`}
              />
            ))}
          </div>
        ) : (
          <EmptyImageState label="追加画像" />
        )}
      </div>
    </div>
  );
}

interface UserDetailPanelProps {
  userId?: string;
  embedded?: boolean;
  onClose?: () => void;
  onUpdated?: () => void;
}

export function UserDetailPanel({
  userId,
  embedded = false,
  onClose,
  onUpdated,
}: UserDetailPanelProps) {
  const navigate = useNavigate();
  const adminProfile = useAuthStore((state) => state.adminProfile);
  const [detail, setDetail] = useState<AdminUserOperationDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [showImpersonationModal, setShowImpersonationModal] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [impersonationReason, setImpersonationReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [impersonationSubmitting, setImpersonationSubmitting] = useState(false);

  const loadUser = useCallback(async () => {
    if (!userId) {
      setError('ユーザーIDが不正です');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const userData = await fetchUserOperationDetail(userId);
      setDetail(userData);
    } catch (loadError) {
      console.error('Error loading user operation detail:', loadError);
      setError('ユーザー詳細の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    void loadUser();
  }, [loadUser]);

  const handleSuspend = async () => {
    if (!userId || !adminProfile?.uid || !suspendReason.trim()) {
      return;
    }

    try {
      setSubmitting(true);
      await suspendUser(userId, suspendReason, adminProfile.uid);
      setShowSuspendModal(false);
      setSuspendReason('');
      await loadUser();
      onUpdated?.();
    } catch (submitError) {
      console.error('Error suspending user:', submitError);
      setError(submitError instanceof Error ? submitError.message : 'アカウント停止に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRestore = async () => {
    if (!userId || !adminProfile?.uid) {
      return;
    }

    try {
      setSubmitting(true);
      await restoreSuspendedUser(userId, adminProfile.uid);
      setShowRestoreModal(false);
      await loadUser();
      onUpdated?.();
    } catch (submitError) {
      console.error('Error restoring user:', submitError);
      setError(submitError instanceof Error ? submitError.message : 'アカウント停止解除に失敗しました');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStartImpersonation = async () => {
    if (!userId || !impersonationReason.trim()) {
      return;
    }

    try {
      setImpersonationSubmitting(true);
      setError(null);
      const session = await startUserImpersonation(userId, impersonationReason);
      const url = buildImpersonationUrl(session.customToken, session.sessionId);
      const opened = window.open(url, '_blank');
      if (!opened) {
        setError('ポップアップがブロックされました。ブラウザ設定を確認してから再度実行してください。');
        return;
      }
      opened.opener = null;
      setShowImpersonationModal(false);
      setImpersonationReason('');
    } catch (submitError) {
      console.error('Error starting impersonation:', submitError);
      setError(submitError instanceof Error ? submitError.message : '代理ログインの開始に失敗しました');
    } finally {
      setImpersonationSubmitting(false);
    }
  };

  if (loading) {
    return <Loading fullScreen={!embedded} message="ユーザー情報を読み込み中..." />;
  }

  if (error || !detail) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-6">
        <p className="text-red-700">{error ?? 'ユーザーが見つかりません'}</p>
        <Button onClick={embedded ? onClose : () => navigate('/users')} className="mt-4">
          一覧に戻る
        </Button>
      </div>
    );
  }

  const { user } = detail;

  return (
    <div className={embedded ? 'space-y-6 p-5' : 'space-y-6'}>
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <Button
            variant="ghost"
            size="sm"
            onClick={embedded ? onClose : () => navigate('/users')}
            leftIcon={<ArrowLeft className="h-4 w-4" />}
          >
            {embedded ? '閉じる' : '戻る'}
          </Button>
          <p className="mt-4 text-[11px] uppercase tracking-[0.18em] text-admin-muted">
            User Detail
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <h1 className="truncate text-2xl font-bold text-admin-ink">
              {user.displayName}
            </h1>
            {user.isDummy === true && (
              <Badge variant="warning" size="sm">
                テスト
              </Badge>
            )}
          </div>
          <p className="mt-1 break-all text-sm text-admin-muted">UID: {user.id}</p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={statusVariant[user.status]}>{USER_STATUS_LABELS[user.status]}</Badge>
          <Button
            variant="secondary"
            onClick={() => setShowImpersonationModal(true)}
            leftIcon={<LogIn className="h-4 w-4" />}
          >
            代理ログイン
          </Button>
          {user.status === 'suspended' ? (
            <Button
              variant="secondary"
              onClick={() => setShowRestoreModal(true)}
              leftIcon={<CheckCircle className="h-4 w-4" />}
            >
              停止解除
            </Button>
          ) : (
            <Button
              variant="danger"
              onClick={() => setShowSuspendModal(true)}
              leftIcon={<Ban className="h-4 w-4" />}
            >
              アカウント停止
            </Button>
          )}
        </div>
      </header>

      {error && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="space-y-4">
        <DetailCard title="基本情報" icon={UserIcon}>
          <DetailTable
            rows={[
              { label: '表示名', value: user.displayName },
              { label: 'メール', value: user.email },
              { label: '種別', value: user.role === 'donor' ? 'ドナー' : 'ドニー' },
              { label: 'テスト', value: user.isDummy === true ? 'テスト' : '-' },
              { label: '本人確認', value: user.identityVerified ? '認証済み' : '未認証' },
              { label: '登録日時', value: user.createdAt?.toDate?.() },
              { label: '最終ログイン', value: user.lastLoginAt?.toDate?.() },
            ]}
          />
        </DetailCard>

        <DetailCard title="対応状態" icon={ClipboardCheck}>
          <DetailTable
            rows={[
              { label: '現在', value: USER_STATUS_LABELS[user.status] },
              {
                label: '停止前',
                value: user.statusBeforeSuspension
                  ? USER_STATUS_LABELS[user.statusBeforeSuspension]
                  : '-',
              },
              { label: '停止日時', value: user.suspendedAt },
              { label: '停止者', value: user.suspendedBy },
              { label: '停止理由', value: user.suspensionReason },
            ]}
          />
        </DetailCard>

        <DetailCard title="プロフィール" icon={FileText}>
          <div className="space-y-5">
            <RecordSection title="公開プロフィール" record={detail.profile?.basicInfo} />
            {detail.profile?.physicalInfo && (
              <RecordSection title="身体的特徴" record={detail.profile.physicalInfo} />
            )}
            {detail.profile?.donationPolicy && (
              <RecordSection title="提供方針" record={detail.profile.donationPolicy} />
            )}
            {detail.profile?.doneePolicy && (
              <RecordSection title="依頼方針" record={detail.profile.doneePolicy} />
            )}
            <div>
              <h3 className="mb-2 text-sm font-semibold text-admin-ink">非公開プロフィール</h3>
              <DetailTable
                rows={[
                  {
                    label: '自由記入',
                    value: detail.privateProfile?.freeText || '未登録',
                  },
                  {
                    label: '追加画像',
                    value: `${detail.privateProfile?.images.length ?? 0}件`,
                  },
                ]}
              />
            </div>
          </div>
        </DetailCard>

        <DetailCard title="画像" icon={ImageIcon}>
          <UserImagesSection
            profileImageUrl={user.iconUrl}
            additionalImages={detail.privateProfile?.images ?? []}
          />
        </DetailCard>

        <DetailCard title="認証・審査" icon={Shield}>
          <DetailTable
            rows={Object.entries(VERIFICATION_LABELS).map(([key, label]) => {
              const item = detail.verification?.items[key];
              return {
                label,
                value: [
                  `状態: ${item?.status ?? 'none'}`,
                  `書類: ${item?.documents ?? 0}件`,
                  `ランク: ${item?.rank ?? '-'}`,
                  `有効期限: ${formatDateTime(item?.expiresAt)}`,
                ].join(' / '),
              };
            })}
          />
        </DetailCard>

          <DetailCard title="問い合わせ" icon={Inbox}>
            <div className="space-y-2">
              {detail.inquiries.length === 0 ? (
                <p className="text-sm text-admin-muted">問い合わせはありません。</p>
              ) : (
                detail.inquiries.slice(0, 8).map((inquiry) => (
                  <Link
                    key={inquiry.id}
                    to={`/inquiries/${inquiry.id}`}
                    className="block rounded-xl border border-admin-border bg-admin-surface p-3 transition hover:border-admin-accent"
                  >
                    <p className="text-sm font-semibold text-admin-ink">
                      {CONTACT_INQUIRY_CATEGORY_LABELS[inquiry.category as ContactInquiryCategory] ?? inquiry.category}
                    </p>
                    <p className="mt-1 line-clamp-2 text-xs text-admin-muted">{inquiry.message}</p>
                    <p className="mt-2 text-xs text-admin-muted">
                      {inquiry.status} / {formatDateTime(inquiry.createdAt)}
                    </p>
                  </Link>
                ))
              )}
            </div>
          </DetailCard>

          <DetailCard title="閲覧申請" icon={Eye}>
            <div className="space-y-2">
              {detail.accessRequests.length === 0 ? (
                <p className="text-sm text-admin-muted">閲覧申請はありません。</p>
              ) : (
                detail.accessRequests.slice(0, 8).map((request) => (
                  <div key={`${request.direction}-${request.id}`} className="rounded-xl border border-admin-border bg-admin-surface p-3">
                    <p className="text-sm font-semibold text-admin-ink">
                      {request.direction === 'sent' ? '送信' : '受信'}: {request.counterpartyName}
                    </p>
                    <p className="mt-1 text-xs text-admin-muted">
                      {request.status} / {formatDateTime(request.requestedAt)}
                    </p>
                  </div>
                ))
              )}
            </div>
          </DetailCard>

          <DetailCard title="会話" icon={MessageSquare}>
            <div className="space-y-2">
              {detail.conversations.length === 0 ? (
                <p className="text-sm text-admin-muted">会話はありません。</p>
              ) : (
                detail.conversations.slice(0, 8).map((conversation) => (
                  <div key={conversation.id} className="rounded-xl border border-admin-border bg-admin-surface p-3">
                    <p className="text-sm font-semibold text-admin-ink">{conversation.counterpartyName}</p>
                    <p className="mt-1 line-clamp-2 text-xs text-admin-muted">
                      {conversation.lastMessage || 'メッセージなし'}
                    </p>
                    <p className="mt-2 text-xs text-admin-muted">
                      未読 {conversation.unreadCount} / {formatDateTime(conversation.lastMessageAt)}
                    </p>
                  </div>
                ))
              )}
            </div>
          </DetailCard>

          <DetailCard title="レビュー" icon={Star}>
            <div className="space-y-2">
              {detail.reviews.length === 0 ? (
                <p className="text-sm text-admin-muted">レビューはありません。</p>
              ) : (
                detail.reviews.slice(0, 8).map((review) => (
                  <div key={`${review.direction}-${review.id}`} className="rounded-xl border border-admin-border bg-admin-surface p-3">
                    <p className="text-sm font-semibold text-admin-ink">
                      {review.direction === 'sent' ? '投稿' : '受領'}: {review.counterpartyName}
                    </p>
                    <p className="mt-1 text-xs text-admin-muted">
                      評価 {review.rating} / {formatDateTime(review.createdAt)}
                    </p>
                    {review.comment && (
                      <p className="mt-2 line-clamp-2 text-xs text-admin-muted">{review.comment}</p>
                    )}
                  </div>
                ))
              )}
            </div>
          </DetailCard>

          <DetailCard title="通知ログ" icon={Mail}>
            <div className="space-y-2">
              {detail.notifications.length === 0 ? (
                <p className="text-sm text-admin-muted">通知ログはありません。</p>
              ) : (
                detail.notifications.slice(0, 8).map((notification) => (
                  <div key={notification.id} className="rounded-xl border border-admin-border bg-admin-surface p-3">
                    <p className="text-sm font-semibold text-admin-ink">{notification.title}</p>
                    <p className="mt-1 text-xs text-admin-muted">
                      {notification.type} / {formatDateTime(notification.sentAt)}
                    </p>
                  </div>
                ))
              )}
            </div>
          </DetailCard>
      </div>

      <Modal
        isOpen={showImpersonationModal}
        onClose={() => setShowImpersonationModal(false)}
        title="代理ログイン"
        size="md"
      >
        <div className="space-y-4">
          <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            対象ユーザーとして app 側へログインします。操作はユーザー本人の操作として反映されます。
          </div>
          <div>
            <label className="mb-2 block text-sm font-semibold text-admin-ink">
              代理操作の理由
            </label>
            <textarea
              value={impersonationReason}
              onChange={(event) => setImpersonationReason(event.target.value)}
              className="w-full rounded-xl border border-admin-border p-3 text-sm focus:border-admin-accent focus:outline-none focus:ring-2 focus:ring-admin-accent/20"
              rows={4}
              placeholder="例: 問い合わせ対応のため、ユーザー画面の表示状態を確認"
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowImpersonationModal(false)}>
              キャンセル
            </Button>
            <Button
              onClick={() => void handleStartImpersonation()}
              isLoading={impersonationSubmitting}
              disabled={!impersonationReason.trim()}
            >
              app を開く
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showSuspendModal}
        onClose={() => setShowSuspendModal(false)}
        title="アカウント停止"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-admin-ink">
            停止前のステータスを保存した上で、このユーザーを停止します。
          </p>
          <textarea
            value={suspendReason}
            onChange={(event) => setSuspendReason(event.target.value)}
            className="w-full rounded-xl border border-admin-border p-3 text-sm focus:border-admin-accent focus:outline-none focus:ring-2 focus:ring-admin-accent/20"
            rows={4}
            placeholder="停止理由を入力してください"
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowSuspendModal(false)}>
              キャンセル
            </Button>
            <Button
              variant="danger"
              onClick={() => void handleSuspend()}
              isLoading={submitting}
              disabled={!suspendReason.trim()}
            >
              停止する
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showRestoreModal}
        onClose={() => setShowRestoreModal(false)}
        title="アカウント停止解除"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-admin-ink">
            停止前のステータスへ戻します。停止前ステータスが記録されていない場合は、メール認証済みに戻します。
          </p>
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={() => setShowRestoreModal(false)}>
              キャンセル
            </Button>
            <Button onClick={() => void handleRestore()} isLoading={submitting}>
              解除する
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

export function UserDetailPage() {
  const { userId } = useParams<{ userId: string }>();
  return <UserDetailPanel userId={userId} />;
}
